import { useState, useRef, useEffect } from "react";
import {
  Sparkles, ArrowRight, Globe, Search, ChevronDown, Crown, TrendingUp,
  Users, Plane, AlertTriangle, Factory, Building2, LineChart, Rocket, Lightbulb, Check,
} from "lucide-react";
import { COUNTRIES } from "../lib/countries";

const ANALYZES = [
  { icon: TrendingUp, label: "Economy" },
  { icon: Users, label: "Population" },
  { icon: Plane, label: "Tourism" },
  { icon: AlertTriangle, label: "Local Problems" },
  { icon: Factory, label: "Popular Industries" },
  { icon: Building2, label: "Existing Businesses" },
  { icon: LineChart, label: "Consumer Trends" },
  { icon: Rocket, label: "Growth Opportunities" },
];

const SAMPLE_IDEAS = [
  ["Marina Booking Platform", 95],
  ["Tourist Luggage Tracker", 94],
  ["Cruise Excursion Marketplace", 93],
  ["School Fee Payment App", 92],
  ["Island Grocery Delivery", 91],
  ["Vacation Property Manager", 90],
];

const STEPS = [
  { icon: Globe, title: "Choose Country", sub: "Tell us where you're building" },
  { icon: Lightbulb, title: "AI Researches Market", sub: "Deep market & industry analysis" },
  { icon: Sparkles, title: "Finds Opportunities", sub: "Identifies gaps & high demand" },
  { icon: Rocket, title: "Generates Ideas", sub: "Unique ideas for your market" },
];

export const CountryIdeasPromo = () => {
  const [selected, setSelected] = useState(COUNTRIES.find((c) => c.name === "Bahamas"));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openBlueprint = () => {
    window.dispatchEvent(new CustomEvent("lbs:open-blueprint", { detail: { mode: "country", country: selected } }));
  };

  const filtered = query.trim()
    ? COUNTRIES.filter((c) => c.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 40)
    : COUNTRIES;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-6" data-testid="home-country-ideas-promo">
      <div className="relative rounded-[2rem] border border-gray-100 bg-white shadow-[0_4px_24px_rgb(0,0,0,0.04)] px-6 md:px-10 py-8 overflow-hidden">
        <div className="flex items-center gap-2 mb-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EDE9FE] text-[#7C3AED] px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide">
            <Sparkles size={12} /> New AI Feature
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          {/* Left: title + country select */}
          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F0F0F] leading-tight">
              AI <span className="text-[#D4AF37]">Country Ideas</span>
            </h2>
            <p className="text-sm md:text-base font-semibold text-[#374151] mt-2">Build a startup designed for your country.</p>
            <p className="text-xs text-[#687280] mt-2 leading-relaxed">
              LaunchBS AI understands your country's economy, industries, tourism, local problems and consumer demand to generate startup ideas that fit your market.
            </p>

            {/* Select Country (the working link into Blueprint AI) */}
            <div className="mt-4 relative" ref={boxRef}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#9aa1ab] mb-1.5 flex items-center gap-1.5"><Globe size={12} className="text-[#D4AF37]" /> Select Country</p>
              <button
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm font-bold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors"
                data-testid="country-promo-select"
              >
                <span>{selected?.flag} {selected?.name}</span>
                <ChevronDown size={16} className={`text-[#9aa1ab] transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="absolute z-30 mt-2 w-full rounded-2xl border border-gray-100 bg-white shadow-xl p-2" data-testid="country-promo-dropdown">
                  <div className="relative mb-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
                    <input
                      autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search Country..."
                      className="w-full rounded-lg border border-gray-200 pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
                      data-testid="country-promo-search"
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {filtered.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => { setSelected(c); setOpen(false); setQuery(""); }}
                        className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium hover:bg-[#FBF7EA] transition-colors ${selected?.code === c.code ? "bg-[#FBF7EA] text-[#8a6d1a]" : "text-[#374151]"}`}
                        data-testid={`country-promo-option-${c.code}`}
                      >
                        {c.flag} {c.name}
                      </button>
                    ))}
                    {filtered.length === 0 && <p className="text-xs text-[#9aa1ab] px-3 py-2">No match.</p>}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-[#9aa1ab] mt-2 flex items-center gap-1.5"><Globe size={11} /> 190+ Countries Supported</p>
            </div>
          </div>

          {/* Center: globe image */}
          <div className="lg:col-span-3 flex justify-center">
            <img
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_idea-hub-409/artifacts/d72exbn7_ChatGPT%20Image%20Aug%204%2C%202026%2C%2010_49_20%20AM.png"
              alt="AI analyzing countries worldwide"
              className="w-full max-w-[300px] h-auto object-contain drop-shadow-xl"
              data-testid="country-promo-globe"
            />
          </div>

          {/* AI Analyzes list */}
          <div className="lg:col-span-3">
            <p className="text-sm font-bold text-[#0F0F0F] mb-2.5">AI Analyzes Your Country</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {ANALYZES.map((a) => (
                <div key={a.label} className="flex items-center gap-2 text-xs text-[#374151]">
                  <span className="w-6 h-6 rounded-lg bg-[#FBF7EA] text-[#8a6d1a] flex items-center justify-center shrink-0"><a.icon size={13} /></span>
                  {a.label}
                </div>
              ))}
            </div>
          </div>

          {/* Right: ideas preview */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-sm font-bold text-[#0F0F0F] flex items-center gap-1.5">{selected?.flag} Startup Ideas</p>
                <span className="text-[9px] font-bold text-[#9aa1ab] uppercase">Opp. Score</span>
              </div>
              <div className="space-y-1.5">
                {SAMPLE_IDEAS.map(([name, score]) => (
                  <div key={name} className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-2.5 py-1.5">
                    <span className="text-xs font-medium text-[#374151] truncate flex items-center gap-1.5"><Rocket size={12} className="text-[#D4AF37] shrink-0" /> {name}</span>
                    <span className="text-xs font-extrabold text-[#8a6d1a] shrink-0">{score}</span>
                  </div>
                ))}
              </div>
              <button onClick={openBlueprint} className="mt-2.5 w-full text-center text-xs font-bold text-[#7C3AED] hover:underline" data-testid="country-promo-viewall">View All 10 Ideas →</button>
            </div>
          </div>
        </div>

        {/* Bottom stepper + CTA */}
        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col xl:flex-row xl:items-center gap-5">
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-full bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0"><s.icon size={15} /></span>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-[#0F0F0F] leading-tight">{s.title}</p>
                  <p className="text-[11px] text-[#9aa1ab] leading-tight mt-0.5">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={openBlueprint}
            data-testid="country-promo-generate-btn"
            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#c5a030] text-[#0F0F0F] px-7 py-3.5 text-sm font-extrabold shadow-[0_12px_30px_rgba(212,175,55,0.35)] hover:-translate-y-0.5 transition-all"
          >
            <Globe size={17} /> Generate My Country Ideas
            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-[#0F0F0F] text-[#D4AF37] px-2 py-0.5 rounded-full"><Crown size={10} /> Premium</span>
          </button>
        </div>
      </div>
    </section>
  );
};
